import $i18n from '@/locales/i18n';
import React, {
  forwardRef,
  Fragment,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import type {
  ChatHandle,
  ChatProps,
  eventName,
  IActionItem,
  IMessageItem,
  supportType,
} from '../types';
// ChatUI依赖(js&css)
import ChatUiChat from '@chatui/core';
// import './chatui.less';
import '@chatui/core/es/styles/index.less';
// 组件依赖
import FancyCard from './components/fancy-card';
import Navbar from './components/nav-bar';
import ComposerInput from './components/composer-input';
import MessageItem from './components/message-item';
// 工具函数
import classnames from 'classnames';
import { isInIframe } from '@/utils/env';
import onClickLink from '@/utils/on-click-link';
import { DEFAULT_PLACE_HOLDER, TEXT_CONTINUE } from '@/utils/constant';
// 样式
import './cn-chatai-ui-chat.scss';
import { CnMessage } from '@cainiaofe/cn-ui';
import QuickReplies from './components/quick-repies';
import StickyBar from './components/sticky-bar';
import { RECORD_STATUS } from './hooks/use-record';

export const Chat = forwardRef((props: ChatProps, ref: Ref<ChatHandle>) => {
  const {
    onSupport,
    messageActions,
    messageIcons,
    onChangeRecommend,
    onSend,
    waiting,
    loading,
    onFlush,
    recordInfo,
    showLoadMore,
  } = props;
  // 输入框的ref
  const composerRef = useRef(null);
  // 消息容器的ref
  const messageRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToEnd: () => {
        messageRef.current?.scrollToEnd();
      },
      setInputText: (value: string) => {
        composerRef.current?.setText(value);
      },
    }),
    [messageRef, composerRef],
  );

  // 发送消息的处理
  const handleSend = useCallback(
    (type: string, value: string, options: Record<any, any>) => {
      onSend(type, value, options);
    },
    [onSend],
  );

  // 点击按钮的回调，对内置类型（比如链接、文本）做了处理，对于事件则抛出到外层
  const handleActionClick = useCallback(
    (eventName: eventName, item: IActionItem, msg: IMessageItem | null) => {
      const { value, type, id } = item;
      if (type === 'link') {
        if (isInIframe) {
          onClickLink(value);
        } else {
          location.href = value;
        }
        return;
      }
      if (type === 'event' && typeof props[eventName] === 'function') {
        props[eventName](item, msg);
        return;
      }
      if (type === 'setInputText') {
        composerRef.current?.setText(value);
        return;
      }
      handleSend('text', value, id ? { id } : {});
    },
    [handleSend, composerRef, props],
  );

  // 输入框禁用文案
  const disableInputHelp = useMemo(() => {
    if (props.disableInput) return props.disableInputHelp;
    return '';
  }, [props.disableInput, props.disableInputHelp]);

  // 最后一条消息的id
  const lastMessageId = useMemo(() => {
    const messages = props.messages || [];
    return messages.length > 0 ? props.messages[props.messages.length - 1]._id : '';
  }, [props.messages]);

  // 最后一个提问的id
  const lastAskId = useMemo(() => {
    const asks = (props.messages || []).filter((i) => i.position === 'right');
    return asks.length > 0 ? asks[asks.length - 1]._id : '';
  }, [props.messages]);

  // 重试动作，获取最后一个提问的内容重新发送
  const handleRetry = useCallback(
    // eslint-disable-next-line
    (msgItem: IMessageItem) => {
      if (loading) return;
      const lastAsk = props.messages.find((_msg) => _msg._id === lastAskId);
      if (!lastAsk) return;
      handleSend('text', lastAsk.content.text, {});
    },
    [handleSend, lastAskId, props.messages, loading],
  );

  // 继续动作，提问”继续“
  const handleContinue = useCallback(
    // eslint-disable-next-line
    (msgItem: IMessageItem) => {
      if (loading) return;
      handleSend('text', TEXT_CONTINUE, {});
    },
    [handleSend, loading],
  );

  const checkIsLoading = useCallback(() => {
    if (loading) {
      CnMessage.notice(
        $i18n.get({
          id: 'PleaseWaitForTheCurrentQuestionT_1276293446',
          dm: '请等待当前问题回答完成',
        }),
      );
      return true;
    }
    return false;
  }, [loading]);

  // 点赞点踩操作
  const handleSupport = useCallback(
    /**
     * @param {IMessageItem} msg 当前消息内容
     * @param {supportType} support 当前点赞/点踩操作类型
     */
    (msg: IMessageItem, support: supportType) => {
      if (checkIsLoading()) {
        return;
      }

      if (typeof onSupport === 'function') {
        onSupport(msg, support);
      }
    },
    [onSupport, checkIsLoading],
  );

  // 复制操作
  const handleCopy = useCallback(
    // eslint-disable-next-line
    (msg: IMessageItem) => {
      // console.log('msg');
    },
    [],
  );

  const stickyBarProps = useMemo(() => {
    if (!recordInfo) {
      return {
        visible: false,
      };
    }
    if (
      recordInfo.recordStatus === RECORD_STATUS.RECORDING ||
      recordInfo.recordStatus === RECORD_STATUS.PAUSED
    ) {
      const textRecording =
        recordInfo.recordStatus === RECORD_STATUS.PAUSED
          ? $i18n.get({ id: 'RecordingPause', dm: '录音暂停' })
          : $i18n.get({ id: 'InTheRecording', dm: '录音中' });
      return {
        visible: true,
        title: `${textRecording} ${recordInfo.recordTime}`,
      };
    }
    return {
      visible: false,
    };
  }, [recordInfo]);

  function renderMessageContent(msg: IMessageItem) {
    const { _id, type, content, position } = msg;
    const {
      showIcons = true,
      showActions = true,
      showSupport: contentShowSupport = true,
      showCopy: contentShowCopy = true,
      messageActions: contentMessageActions,
      messageIcons: contentMessageIcons,
      noBubble,
    } = content;
    const msgItem = {
      _id,
      text: content.text,
      type,
      code: content.code,
      data: content.data,
    };

    return (
      <MessageItem
        message={msg}
        text={content.text}
        type={type}
        code={content.code}
        data={content.data}
        renderContent={content.renderContent}
        recommendTitle={content.recommendTitle}
        recommendList={content.recommendList}
        recommendTotalRows={content.totalRows}
        showChangeRecommend={content.showChangeRecommend}
        formatUserMessage={props.formatUserMessage}
        format={content.format}
        _id={_id}
        position={position}
        support={content.support}
        loading={loading}
        waiting={waiting}
        isLastAnswer={_id && _id === lastMessageId}
        onRetry={() => {
          handleRetry(msgItem);
        }}
        onContinue={() => {
          handleContinue(msgItem);
        }}
        onSupport={(_support) => {
          handleSupport(msgItem, _support);
        }}
        showActions={showActions}
        showIcons={showIcons}
        messageActions={contentMessageActions || messageActions}
        messageIcons={contentMessageIcons || messageIcons}
        onClickMessageAction={(item) => {
          if (checkIsLoading()) {
            return;
          }
          handleActionClick('onClickMessageAction', item, msgItem);
        }}
        onClickMessageIcon={(item) => {
          if (checkIsLoading()) {
            return;
          }
          handleActionClick('onClickMessageIcon', item, msgItem);
        }}
        onClickRecommend={(item) => {
          handleActionClick('onClickRecommendItem', item, msgItem);
        }}
        onChangeRecommend={(data) => {
          if (typeof onChangeRecommend === 'function') {
            onChangeRecommend(msgItem, data.pageNum, data.pageSize);
          }
        }}
        showRetry={props.showRetry}
        showContinue={props.showContinue}
        showCopy={contentShowCopy && props.showCopy}
        showSupport={contentShowSupport && props.showSupport}
        noBubble={noBubble}
        onCopy={() => {
          handleCopy(msgItem);
        }}
      />
    );
  }

  // className 命名为 cn-xxx
  return (
    <FancyCard
      className={classnames('chatai-ui-chat', { loadmore: showLoadMore }, props.className)}
    >
      <ChatUiChat
        renderNavbar={() => {
          return (
            <Fragment>
              {props.showNavBar ? (
                <Navbar
                  logo={props.navBarLogo}
                  title={props.navBarTitle}
                  buttons={props.navBarButtons}
                  actions={props.navBarActions}
                  onClickButton={(item) => {
                    handleActionClick('onClickNavBarButton', item, null);
                  }}
                  onClickAction={(item) => {
                    handleActionClick('onClickNavBarAction', item, null);
                  }}
                />
              ) : null}
              <StickyBar {...stickyBarProps} />
            </Fragment>
          );
        }}
        messagesRef={messageRef}
        isTyping={waiting}
        loadMoreText={$i18n.get({ id: 'ClickToLoadMore', dm: '点击加载更多' })}
        onRefresh={props.onRefresh}
        Composer={ComposerInput}
        composerRef={composerRef}
        messages={props.messages}
        renderMessageContent={renderMessageContent}
        // @ts-ignore
        onSend={(from, type, value, options) => {
          handleSend(type, value, options);
        }}
        inputOptions={{
          // @ts-ignore
          hideInput: props.hideInput,
          disableInput: loading || props.disableInput,
          disableInputHelp,
          showFlushButton: props.showFlushButton,
          minRows: props.minRows,
        }}
        placeholder={props.placeholder || DEFAULT_PLACE_HOLDER}
        renderQuickReplies={() => (
          <QuickReplies
            loading={loading}
            dataSource={props.suggestions}
            showStopAnswer={props.showStopAnswer}
            onClick={(item) => {
              handleActionClick('onClickQuickReply', item, null);
            }}
            recordStatus={recordInfo?.recordStatus}
            stopRecord={recordInfo?.stopRecord}
            continueRecord={recordInfo?.continueRecord}
            pauseRecord={recordInfo?.pauseRecord}
          />
        )}
        // @ts-ignore
        toolbar={props.toolbar}
        onToolbarClick={(item) => {
          if (item.type === 'flush') {
            if (typeof onFlush === 'function') {
              onFlush();
            }
          } else {
            // @ts-ignore
            handleActionClick('onClickToolbarItem', item, null);
          }
        }}
      />
    </FancyCard>
  );
});

Chat.displayName = 'Chat';
